import { FilePlusIcon, MenuIcon, MoonIcon, PlusIcon, SearchIcon, SunIcon } from "lucide-react";
import type { Me } from "@/api/me";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SimpleTooltip } from "@/components/ui/primitives";
import { useEntitiesMeta } from "@/entities/api";
import { entityIcon } from "@/entities/icons";
import { useQuickCreate } from "@/entities/quick-create";
import { useTheme } from "@/lib/theme";
import { NotificationBell } from "@/notifications/NotificationBell";
import { UserMenu } from "./UserMenu";

interface TopBarProps {
  me: Me;
  onOpenCommandPalette: () => void;
  onOpenMobileNav: () => void;
}

const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform || navigator.userAgent);

export function TopBar({ me, onOpenCommandPalette, onOpenMobileNav }: TopBarProps) {
  const entities = useEntitiesMeta();
  const { openCreate } = useQuickCreate();
  const creatable = (entities.data ?? []).filter((e) => e.can_create);

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-background/85 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-background/70 md:px-6">
      <Button variant="ghost" size="icon-sm" className="md:hidden" aria-label="Open navigation" onClick={onOpenMobileNav}>
        <MenuIcon />
      </Button>

      <button
        type="button"
        onClick={onOpenCommandPalette}
        className="flex h-8 w-full max-w-sm items-center gap-2 rounded-md border border-input bg-card px-2.5 text-[13px] text-muted-foreground shadow-xs transition-colors outline-none hover:border-ring/40 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40"
      >
        <SearchIcon className="size-4" aria-hidden="true" />
        <span className="flex-1 truncate text-left">Search or jump to…</span>
        <span className="hidden items-center gap-0.5 sm:flex">
          <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <div className="ml-auto flex items-center gap-1">
        <DropdownMenu>
          <SimpleTooltip label="Create">
            <DropdownMenuTrigger asChild>
              <Button size="icon-sm" aria-label="Create new">
                <PlusIcon />
              </Button>
            </DropdownMenuTrigger>
          </SimpleTooltip>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Create new</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {entities.isPending ? (
              <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
            ) : creatable.length === 0 ? (
              <DropdownMenuItem disabled>
                <FilePlusIcon /> Nothing you can create yet
              </DropdownMenuItem>
            ) : (
              creatable.map((e) => {
                const Icon = entityIcon(e.name);
                return (
                  <DropdownMenuItem key={e.name} onSelect={() => openCreate(e.name)}>
                    <Icon /> {e.label}
                  </DropdownMenuItem>
                );
              })
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <ThemeToggle />

        <NotificationBell />

        <div className="ml-1">
          <UserMenu me={me} />
        </div>
      </div>
    </header>
  );
}

/** Visible light/dark switch. Cycles light ↔ dark; "System" stays available in the user menu. */
export function ThemeToggle() {
  const { resolvedTheme, toggleTheme } = useTheme();
  const next = resolvedTheme === "dark" ? "light" : "dark";
  return (
    <SimpleTooltip label={`Switch to ${next} theme`}>
      <Button variant="ghost" size="icon-sm" aria-label={`Switch to ${next} theme`} onClick={toggleTheme}>
        {resolvedTheme === "dark" ? <SunIcon /> : <MoonIcon />}
      </Button>
    </SimpleTooltip>
  );
}
